import { AuthState } from 'core/context/reducer';
import { ACCESS_KEY, BASE_URL, Data, IAdditionalInformation, IUser, NODE_ENV } from 'core/utilities';
import { HTTPMethod, mutate, Result } from '.';
import { axios } from 'core/api';
import { isSuccessCode } from './api';

export const updateUserAddress = async (addressId : string, payload : Data) : Promise<Result> => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        const { success } = await mutate(
            `api/v1/user/address/${addressId}/`, 
            payload,
            { 'Authorization' : `Bearer ${token}` },
            HTTPMethod.PATCH
        );

        return {
            success
        };
    }

    return {
        success : false
    };
};

export const getUserAddress = async (state: AuthState) => {
    const { token } = state;

    if (!token) {
        return {
            success : false,
            error   : 'Invalid authentication token.'
        };
    }

    const options = {
        method : HTTPMethod.GET,
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        }
    };

    try {
        const response = await fetch(`${BASE_URL}/api/v1/user/address/`, options);
        const { results } = await response.json();

        if (results.length < 1) throw new Error('User address not found');

        const userAddress = results[0];

        return {
            success: true,
            userAddress
        };
    } catch (error) {
        console.log(error);
        return {
            success : false,
            error   : error as string
        };
    }
};

export const createHubspotDeal = async (payload : Data) : Promise<Result> => {
    // Disable hubspot triggers except when in production
    if (NODE_ENV !== 'production') return { success : true };

    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        return await mutate(
            'api/v1/hubspot',
            payload,
            { 'Authorization' : `Bearer ${token}` }
        );
    }

    return {
        success : false
    };
};

export const resendVerify = async (state : AuthState) => {
    const { token } = state;
    if (!token) {
        return {
            success : false,
            error   : 'Invalid authentication token.'
        };
    }

    const options = {
        method : 'GET',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        }
    };

    try {
        await fetch(`${BASE_URL}/api/v1/resend-verify`, options);

        return {
            success : true
        };
    } catch (error) {
        return {
            success : false,
            error   : error as string
        };
    }
};

export const verifyEmail = async (payload : Data) : Promise<Result> => {
    return await mutate(
        'api/verify-email',
        payload
    );
};

interface UserResult extends Result 
{
    user? : IUser;
}

export const getUser = async (state : AuthState) : Promise<UserResult> => {
    const { id, token } = state;
    if (!id || !token) {
        return {
            success : false,
            error   : 'Invalid user id or token.'
        };
    }

    const options = {
        method : 'GET',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        }
    };

    try {
        const response = await fetch(`${BASE_URL}/api/v1/user/${id}/`, options);
        const user = await response.json();

        const { 
            email,
            first_name, 
            last_name,
            dob,
            waitlist,
            is_verified
        } = user;
        const formatName = (v : string) => `${v.slice(0,1).toUpperCase()}${v.slice(1).toLowerCase()}`;
        const fullname = `${formatName(first_name)} ${formatName(last_name)}`;

        return {
            success : true,
            user : {
                email,
                first_name,
                fullname,
                dob,
                waitlist,
                verified: Boolean(is_verified)
            }
        };
    } catch (error) {
        return {
            success : false,
            error   : error as string
        };
    }
};

export const getUserPhoneNumber = async (state: AuthState) => {
    const { token } = state;

    if (!token) {
        return {
            success : false,
            error   : 'Invalid authentication token.'
        };
    }

    const options = {
        method : HTTPMethod.GET,
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        }
    };

    try {
        const response = await fetch(`${BASE_URL}/api/v1/user/phonenumber/`, options);
        const { results } = await response.json();

        if (results.length < 1) throw new Error('User address not found');

        const phoneNumber = results[0];

        return {
            success: true,
            phoneNumber
        };
    } catch (error) {
        console.log(error);
        return {
            success : false,
            error   : error as string
        };
    }
};

export const updateUserPhoneNumber = async (phoneId: string, payload: any) => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (!token) {
        return {
            success : false,
            error   : 'Invalid user id or token.'
        };
    }

    const { success } = await mutate(
        `api/v1/user/phonenumber/${phoneId}/`, 
        payload,
        { 'Authorization' : `Bearer ${token}` },
        HTTPMethod.PATCH
    );

    return {
        success
    };
};

interface HubspotProps {
    formId: string;
    portalId: string;
    body: any;
}

export const hubspotRegisterForm = async ({ portalId, formId, body }: HubspotProps) => {
    const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    };

    try {
        const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, options);
        return response;
    } catch (error) {
        return error;
    }
};

export const getAdditionalInformationRequest = async (): Promise<{ success: boolean, questions: IAdditionalInformation[] }> => {
    const response = await axios.get('/api/v1/applicant/additional-info/');

    const { status, data } = response;

    if (isSuccessCode(status)) {
        return {
            success: true,
            questions: data
        };
    } else {
        return {
            success: false,
            questions: []
        };
    }
};

export const answerAdditionalInformationRequest = async (infoId: number, answer: string) => {
    const response = await axios.patch(`/api/v1/applicant/additional-info/${infoId}/`, { answer });

    const { status } = response;

    if (isSuccessCode(status)) {
        return {
            success: true
        };
    } else {
        return {
            success: false
        };
    }
};
